import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import API_BASE from './apiBase';
import TermsAndConditions from './terms and conditions';


function WebAppConsent() {
  const [loading, setLoading] = useState(true);
  // const [hasConsent, setHasConsent] = useState(false);
  // consentStatus: 'unknown' | 'none' | 'existing' | 'just_granted'
  const [consentStatus, setConsentStatus] = useState('unknown');
  const [consentData, setConsentData] = useState(null);
  const [appName, setAppName] = useState('');
  const [redirectUrl, setRedirectUrl] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [userPhone, setUserPhone] = useState('');
  const [userDOB, setUserDOB] = useState('');
  const [userAddress, setUserAddress] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Parse URL parameters
    const params = new URLSearchParams(window.location.search);
    const forceNew = params.get('force') === '1' || params.get('force_new') === '1' || params.get('force') === 'true';
    const app = params.get('app') || 'Web Application';
    const redirect = params.get('redirect') || '';
    const email = params.get('email') || '';
    const tenantId = params.get('tenant_id') || params.get('tenantId');
    
    setAppName(app);
    setRedirectUrl(redirect);
    setUserEmail(email);
    // expose force flag to state so checkExistingConsent can use it
    if (forceNew) {
      try { localStorage.setItem('webapp_consent_force', '1'); } catch (e) {}
    } else {
      try { localStorage.removeItem('webapp_consent_force'); } catch (e) {}
    }

    // Store tenant_id in state or localStorage for later use
    if (tenantId) {
      try {
        localStorage.setItem('webapp_consent_tenant_id', tenantId);
      } catch (e) {
        console.warn('Could not store tenant_id', e);
      }
    }

    // If email provided, check if consent already exists
    if (email) {
      // checkExistingConsent(email, app);
      const force = localStorage.getItem('webapp_consent_force') === '1';
      checkExistingConsent(email, app, force);
    } else {
      setLoading(false);
      setConsentStatus('none');
    }

    // Listen for init payload from opener (when popup opened with a short URL)
    function _initMessageHandler(event) {
      try {
        if (!event || !event.data) return;
        if (event.data && event.data.type === 'INIT_CONSENT') {
          const d = event.data;
          if (d.email) setUserEmail(d.email);
          if (d.redirect) setRedirectUrl(d.redirect);
          try {
            if (d.tenant_id) localStorage.setItem('webapp_consent_tenant_id', d.tenant_id);
            if (d.force) localStorage.setItem('webapp_consent_force', '1');
            else localStorage.removeItem('webapp_consent_force');
          } catch (e) {}
          // Trigger consent check with explicit force flag
          try { checkExistingConsent(d.email || '', d.app || app, !!d.force); } catch (e) {}
        }
      } catch (e) {}
    }
    window.addEventListener('message', _initMessageHandler);

    return () => {
      try { window.removeEventListener('message', _initMessageHandler); } catch (e) {}
    };
  }, []);

  const checkExistingConsent = async (email, app, forceFlag) => {
    try {
      const response = await axios.get(`${API_BASE}/user/consent/check`, {
        params: {
          email: email,
          app_name: app,
          consent_type: 'Web App Access'
        }
      });
      // Prefer explicit forceFlag passed from URL parsing. Fall back to
      // localStorage for backwards compatibility.
      var force = false;
      try {
        if (typeof forceFlag !== 'undefined') {
          force = !!forceFlag;
        } else {
          force = localStorage.getItem('webapp_consent_force') === '1';
        }
      } catch (e) { force = false; }
      if (response.data && response.data.has_consent) {
        
        if (!force) {
          if (consentStatus !== 'just_granted') {
            setConsentStatus('existing');
          }
          setConsentData(response.data.consent);
          // Auto-redirect if consent exists
          if (redirectUrl) {
            setTimeout(() => {
              window.location.href = `${redirectUrl}?consent_token=${response.data.token || 'valid'}`;
            }, 2000);
          }
        } else {
          // Clear any prior status so the form is visible and user can submit a new request
          setConsentStatus('none');
          // keep consentData available for reference
          setConsentData(response.data.consent);
          // remove force flag after using it
          try { localStorage.removeItem('webapp_consent_force'); } catch (e) {}
        }
      }
    } catch (error) {
      console.error('Error checking consent:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitConsent = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (submitting) {
      return;
    }
    
    if (!userEmail || !userName || !userPhone || !userDOB || !userAddress) {
      Swal.fire({
        text: 'Please fill all required fields',
        icon: 'warning'
      });
      return;
    }

    if (!acceptedTerms) {
      Swal.fire({
        text: 'Please accept the Terms and Conditions to continue.',
        icon: 'warning'
      });
      return;
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userEmail)) {
      Swal.fire({ text: 'Please enter a valid email', icon: 'warning' });
      return;
    }

    // Validate phone (10 digits, starts with 6-9)
    const phoneRegex = /^[6-9]\d{9}$/;
    if (!phoneRegex.test(userPhone)) {
      Swal.fire({ text: 'Phone must be 10 digits and start with 6-9', icon: 'warning' });
      return;
    }

    setSubmitting(true);

    try {
      // Get tenant_id from localStorage or URL or use default
      let tenantId = null;
      try {
        tenantId = localStorage.getItem('webapp_consent_tenant_id') || 
                   localStorage.getItem('tenant_id') ||
                   new URLSearchParams(window.location.search).get('tenant_id');
      } catch (e) {
        console.warn('Could not retrieve tenant_id', e);
      }
      
      // If still no tenant_id, use a default value (1)
      if (!tenantId) {
        tenantId = '1';
      }

      const payload = {
        email: userEmail,
        username: userName,
        phone: userPhone,
        dob: userDOB,
        address: userAddress,
        type: 'Web App Access',
        entity: 'employee',
        appName: appName,
        consent: `I consent to use ${appName} and agree to the terms and conditions for data processing.`,
        action: 'granted',
        status: 'active',
        userAction: 'web_app_login',
        tenant_id: parseInt(tenantId) || 1,
        data: JSON.stringify({
          source: 'web_app_consent_page',
          app: appName,
          timestamp: new Date().toISOString(),
          accepted_terms: acceptedTerms
        })
      };

      const response = await axios.post(`${API_BASE}/user/consent`, payload);
      const wasSuccessful = response && response.status === 200;

      if (wasSuccessful) {
        Swal.fire({
          text: 'Purpose of Data Collection!',
          icon: 'success',
          timer: 2000
        });
        

        // setHasConsent(true);
        setConsentStatus('just_granted');
        setConsentData(response.data || null);

        // Redirect back to the application
        if (redirectUrl) {
          setTimeout(() => {
            const token = response.data?.consent_token || response.data?.id || 'valid';
            window.location.href = `${redirectUrl}?consent_token=${token}`;
          }, 2000);
        }

        // Post message to parent window if embedded in iframe, or to opener if opened as popup
        const msg = {
          type: 'CONSENT_GRANTED',
          token: response.data?.consent_token || response.data?.id || null,
          email: userEmail,
          app: appName
        };
        try {
          if (window.parent !== window) {
            window.parent.postMessage(msg, '*');
          }
        } catch (e) {
          // ignore
        }
        try {
          if (window.opener && !window.opener.closed) {
            window.opener.postMessage(msg, '*');
            // Close popup after a short delay so parent can receive the message and popup shows "Consent Recorded" briefly
            setTimeout(() => {
              try { window.close(); } catch (e) {}
            }, 700);
          }
        } catch (e) {
          // ignore
        }
      } else {
        throw new Error(response.data?.detail || 'Failed to save consent');
      }
    } catch (error) {
      console.error('Error submitting consent:', error);
      
      // Extract detailed error message
      let errorMsg = 'Error saving consent';
      if (error?.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      } else if (error?.response?.data) {
        errorMsg = typeof error.response.data === 'string' 
          ? error.response.data 
          : JSON.stringify(error.response.data);
      } else if (error?.message) {
        errorMsg = error.message;
      }
      
      Swal.fire({
        text: errorMsg,
        icon: 'error',
        confirmButtonText: 'OK'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeny = (e) => {
    e && e.preventDefault && e.preventDefault();
    // Show denied state locally
    setConsentStatus('denied');

    // Notify parent/opener that consent was denied
    const msg = {
      type: 'CONSENT_DENIED',
      email: userEmail,
      app: appName
    };
    try {
      if (window.parent !== window) {
        window.parent.postMessage(msg, '*');
      }
    } catch (err) {}
    try {
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage(msg, '*');
        // close popup after brief delay
        setTimeout(() => { try { window.close(); } catch (e) {} }, 700);
      }
    } catch (err) {}
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.spinner}></div>
          <p>Checking consent status...</p>
        </div>
      </div>
    );
  }
  // if(hasConsent ){
  if (consentStatus === 'existing') {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.successIcon}>✓</div>
          <h2>Consent Already Granted</h2>
          <p>You have already provided consent for {appName}.</p>
          {redirectUrl && <p>Redirecting you back...</p>}
        </div>
      </div>
    );
  }

  if (consentStatus === 'just_granted') {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.successIcon}>✓</div>
          <h2>Consent Recorded</h2>
          <p>Thank you — your consent for {appName} has been recorded.</p>
          {redirectUrl && <p>Redirecting you back…</p>}
        </div>
      </div>
    );
  }

  if (consentStatus === 'denied') {
    return (
      <div style={styles.container}>
        <div style={{ ...styles.card, backgroundColor: '#fff5f5', border: '1px solid #fee2e2' }}>
          <div style={{ fontSize: 48, color: '#dc2626', marginBottom: 20 }}>✕</div>
          <h2>Consent Denied</h2>
          <p>You have declined consent for {appName}.</p>
          {redirectUrl && <p>Redirecting you back…</p>}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h2 style={styles.title}>Consent Required</h2>
        <p style={styles.subtitle}>
          {appName} requires your consent to proceed
        </p>

        <form onSubmit={handleSubmitConsent} style={styles.form}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Email *</label>
            <input
              type="email"
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
              style={styles.input}
              placeholder="your.email@example.com"
              required
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Full Name *</label>
            <input
              type="text"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              style={styles.input}
              placeholder="John Doe"
              required
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Phone Number *</label>
            <input
              type="tel"
              value={userPhone}
              onChange={(e) => setUserPhone(e.target.value)}
              style={styles.input}
              placeholder="9876543210"
              required
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Date of Birth *</label>
            <input
              type="date"
              value={userDOB}
              onChange={(e) => setUserDOB(e.target.value)}
              style={styles.input}
              required
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Address *</label>
            <input
              type="text"
              value={userAddress}
              onChange={(e) => setUserAddress(e.target.value)}
              style={styles.input}
              placeholder="Enter your address"
              required
            />
          </div>

          <div style={styles.consentBox}>
            <p style={styles.consentText}>
              By proceeding, I consent to use <strong>{appName}</strong> and agree to the 
              terms and conditions for data processing.
            </p>
            <div style={styles.termsRow}>
              <label style={styles.termsLabel}>
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  style={styles.checkbox}
                />
                I have read and agree to the Terms and Conditions
              </label>
              <button
                type="button"
                style={styles.termsLink}
                onClick={() => setShowTermsModal(true)}
              >
                View Terms & Conditions
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            style={{
              ...styles.submitButton,
              opacity: submitting ? 0.6 : 1,
              cursor: submitting ? 'not-allowed' : 'pointer'
            }}
          >
            {submitting ? 'Processing...' : 'Grant Consent & Continue'}
          </button>
        </form>
      </div>

      {showTermsModal && (
        <div style={styles.modalOverlay} onClick={() => setShowTermsModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <TermsAndConditions onBack={() => setShowTermsModal(false)} />
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    padding: '20px',
    fontFamily: 'Arial, sans-serif'
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    padding: '40px',
    maxWidth: '500px',
    width: '100%',
    textAlign: 'center'
  },
  title: {
    fontSize: '24px',
    marginBottom: '10px',
    color: '#333'
  },
  subtitle: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '30px'
  },
  form: {
    textAlign: 'left'
  },
  formGroup: {
    marginBottom: '20px'
  },
  label: {
    display: 'block',
    marginBottom: '5px',
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#333'
  },
  input: {
    width: '100%',
    padding: '10px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    boxSizing: 'border-box'
  },
  consentBox: {
    backgroundColor: '#f9f9f9',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    padding: '15px',
    marginBottom: '20px'
  },
  consentText: {
    fontSize: '13px',
    color: '#555',
    lineHeight: '1.5',
    margin: '0 0 10px 0'
  },
  termsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
    flexWrap: 'wrap'
  },
  termsLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: '#333',
    cursor: 'pointer'
  },
  checkbox: {
    width: '15px',
    height: '15px',
    cursor: 'pointer'
  },
  termsLink: {
    background: 'transparent',
    border: 'none',
    color: '#007bff',
    cursor: 'pointer',
    padding: 0,
    fontSize: '13px',
    textDecoration: 'underline'
  },
  submitButton: {
    width: '100%',
    padding: '12px',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'background-color 0.3s'
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    padding: '20px',
    zIndex: 1000,
    overflowY: 'auto'
  },
  modalContent: {
    width: '100%',
    maxWidth: '800px',
    maxHeight: '90vh',
    overflowY: 'auto',
    borderRadius: '8px',
    backgroundColor: 'transparent'
  },
  spinner: {
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #007bff',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 20px'
  },
  successIcon: {
    fontSize: '48px',
    color: '#28a745',
    marginBottom: '20px'
  }
};

export default WebAppConsent;
